import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

export type WizardStepsVariant = 'numbered' | 'bars';

/**
 * Step header of multi-step forms. Desktop: numbered steps, or a progress bar per step (`variant="bars"`);
 * mobile: "Step n of N" with a progress bar. Steps up to `maxReachable` can be clicked; labels may be
 * translation keys. States of a step:
 * - done: before the current one, or finished earlier (before `maxReachable`) when going back;
 * - editing: the current one while later steps are done already, i.e. a finished step is being changed;
 * - pending: the furthest step reached, left unfinished to go back to an earlier one. `completed` marks every step done and locks them,
 * e.g. after a form was sent; the mobile label then shows `completedLabel`.
 */
@Component({
  selector: 'wizard-steps',
  templateUrl: './wizard-steps.component.html',
  styleUrls: ['./wizard-steps.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WizardStepsComponent {
  steps = input.required<string[]>();
  current = input.required<number>();
  maxReachable = input<number>(0);
  variant = input<WizardStepsVariant>('numbered');
  completed = input<boolean>(false);
  completedLabel = input<string>('');

  stepClick = output<number>();

  progress = computed(() =>
    this.completed() ? 100 : Math.round(((this.current() + 1) / Math.max(this.steps().length, 1)) * 100)
  );

  /** Steps passed with "next": all before the furthest reached one, except the one shown now. */
  isDone(index: number): boolean {
    if (this.completed()) return true;
    return index !== this.current() && index < Math.max(this.current(), this.maxReachable());
  }

  isEditing(index: number): boolean {
    return !this.completed() && index === this.current() && this.current() < this.maxReachable();
  }

  isPending(index: number): boolean {
    return !this.completed() && index === this.maxReachable() && index > this.current();
  }

  /** The current step is a finished one being changed. */
  editing = computed(() => this.isEditing(this.current()));

  isDisabled(index: number): boolean {
    return this.completed() || index > this.maxReachable();
  }

  onStepClick(index: number): void {
    if (this.isDisabled(index) || index === this.current()) return;
    this.stepClick.emit(index);
  }
}
